import React from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../content/ContentContext';
import { renderMultiline } from '../utils/text';
import CtaBand from '../components/CtaBand';

const Home: React.FC = () => {
  const { t, img } = useContent();

  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="hero__bg"><img src={img('home.hero.image', '/img/03-sandalion-1024x576.jpg')} alt="Enduro nella natura della Sardegna" /></div>
        <div className="hero__grid" />
        <div className="hero__inner">
          <div className="hero__top">
            <span>{t('home.hero.top.left', 'Moto Club · Est. 2020')}</span>
            <span>{t('home.hero.top.right', 'Olbia — Sardegna')}</span>
          </div>
          <span className="eyebrow on-dark">{t('home.hero.eyebrow', 'Alla scoperta della Sardegna nascosta')}</span>
          <h1 className="display">{renderMultiline(t('home.hero.title', 'Insolita\nSardegna'))}</h1>
          <p className="hero__sub">
            {t('home.hero.subtitle', "Tour in fuoristrada, enduro e 4×4 tra sentieri selvaggi, coste incontaminate e villaggi dimenticati. Dove finisce l'asfalto, inizia l'avventura.")}
          </p>
          <div className="hero__cta">
            <Link to="/tour" className="btn btn-acid">{t('home.hero.cta1', 'Scopri i Tour')}</Link>
            <Link to="/rally-raid" className="btn btn-ghost">{t('home.hero.cta2', 'Sardegna Rally Raid 2026')}</Link>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="sec">
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">{t('home.club.eyebrow', 'Il Club')}</span>
            <span className="idx">{t('home.club.idx', 'Moto Club · Olbia')}</span>
          </div>
          <div className="manifesto">
            <p className="manifesto__big reveal">
              {renderManifesto(t('home.manifesto.big', "La Sardegna è un'isola straordinaria dalla bellezza mozzafiato. Ma se vuoi davvero scoprirne i tesori nascosti, devi farlo in moto, dove finisce la strada."))}
            </p>
            <div className="manifesto__side reveal" data-d="1">
              <p>{t('home.manifesto.p1', 'Siamo appassionati di motori e amanti dell\'avventura. La miglior soluzione per una gita in fuoristrada o su enduro: percorsi accidentati alla scoperta di sentieri "selvaggi" nel cuore verde della Sardegna.')}</p>
              <p>{t('home.manifesto.p2', 'Immergiti nella natura, tra montagne impervie, calette cristalline e siti archeologici millenari, con panorami che ti toglieranno il fiato.')}</p>
              <Link to="/storia" className="btn btn-ghost-ink">{t('home.manifesto.cta', 'La nostra storia')}</Link>
            </div>
          </div>
          <div className="stats">
            <div className="stat reveal">
              <b data-count={t('home.stat1.value', '300')} data-suffix="+">0</b>
              <span>{t('home.stat1.label', 'Km di prove speciali')}</span>
            </div>
            <div className="stat reveal" data-d="1">
              <b data-count={t('home.stat2.value', '6')}>0</b>
              <span>{t('home.stat2.label', 'Anni di avventure')}</span>
            </div>
            <div className="stat reveal" data-d="2">
              <b data-count={t('home.stat3.value', '100')} data-suffix="%">0</b>
              <span>{t('home.stat3.label', 'Sardegna autentica')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* EXPERIENCES */}
      <section className="sec sec--ink">
        <div className="wrap">
          <div className="sec-head reveal">
            <div>
              <span className="eyebrow on-dark">{t('home.experiences.eyebrow', "Vivi l'esperienza")}</span>
              <h2 className="h-sec" style={{ marginTop: '.35em' }}>{renderMultiline(t('home.experiences.title', "Tre modi\ndi correre l'isola"))}</h2>
            </div>
            <span className="idx">{t('home.experiences.idx', "Più percorsi, un'unica passione")}</span>
          </div>
          <div className="cards">
            <article className="card card--a reveal">
              <div className="card__img"><img src={img('home.card.rally.image', '/img/extreme-insolita.jpg')} alt="Gare Insolita Sardegna" /></div>
              <div className="card__top"><span>{t('home.card.gare.kicker', 'Gare · Eventi')}</span><span>{t('home.card.gare.dates', 'Sandalion · Ott 2026')}</span></div>
              <div className="card__body">
                <h3>{renderMultiline(t('home.card.gare.title', 'Le nostre\nGare'))}</h3>
                <p>{t('home.card.gare.text', 'La grande stagione delle gare: il Sandalion Rally di ottobre 2026 e il Sardegna Rally Raid. Manifestazioni di livello nazionale e internazionale nel cuore del fuoristrada.')}</p>
                <Link to="/gare" className="card__link">{t('home.card.gare.link', 'Scopri le gare')}</Link>
              </div>
            </article>
            <article className="card card--b reveal" data-d="1">
              <div className="card__img"><img src={img('home.card.club.image', '/img/insolita-sardegna-moto.jpg')} alt="Entra nel Moto Club" /></div>
              <div className="card__top"><span>{t('home.card.club.kicker', 'Community')}</span><span>{t('home.card.club.dates', 'Tesseramento 2026')}</span></div>
              <div className="card__body">
                <h3>{renderMultiline(t('home.card.club.title', 'Entra nel\nMoto Club'))}</h3>
                <p>{t('home.card.club.text', 'È partita la campagna tesseramenti 2026. Unisciti alla famiglia Insolita Sardegna: la tua nuova casa su due ruote.')}</p>
                <Link to="/iscrizioni" className="card__link">{t('home.card.club.link', 'Tesserati ora')}</Link>
              </div>
            </article>
            <article className="card card--c reveal" data-d="2">
              <div className="card__img"><img src={img('home.card.trans.image', '/img/allenamento-2022-insolita-sardegna.jpg')} alt="Transardegna" /></div>
              <div className="card__top"><span>{t('home.card.trans.kicker', 'Tour')}</span><span>{t('home.card.trans.dates', 'Roadbook / GPS')}</span></div>
              <div className="card__body">
                <h3>{renderMultiline(t('home.card.trans.title', 'Trans\nardegna'))}</h3>
                <p>{t('home.card.trans.text', "Tour Enduro con percorso GPS o Roadbook. Sei giorni con partenza e arrivo a Olbia, attraversando l'isola da nord a sud.")}</p>
                <Link to="/tour" className="card__link">{t('home.card.trans.link', 'Vedi i tour')}</Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* SPLIT */}
      <section className="sec">
        <div className="wrap">
          <div className="split">
            <div className="split__media reveal">
              <img src={img('home.split.image', '/img/insolita-sardegna-experience.jpg')} alt="Moto tour Insolita Sardegna" />
              <span className="frame" />
              <span className="tag">Moto Tour — Insolita</span>
            </div>
            <div className="reveal" data-d="1">
              <span className="eyebrow">{t('home.split.eyebrow', 'Percorsi')}</span>
              <h2 className="h-sec" style={{ margin: '.35em 0 .5em' }}>{renderMultiline(t('home.split.title', 'Il terreno\ndi gioco perfetto'))}</h2>
              <p className="lead">{t('home.split.lead', "La Sardegna è l'ideale per questo tipo di attività: dal mare all'entroterra, una vasta gamma di percorsi off-road tra spiagge remote, villaggi nascosti tra le colline e siti archeologici antichi.")}</p>
              <ul className="feat-list">
                <li><span className="t"><b>{t('home.split.f1.title', 'Tour 4×4')}</b><span>{t('home.split.f1.text', 'Escursioni in fuoristrada per gruppi, coppie e famiglie.')}</span></span></li>
                <li><span className="t"><b>{t('home.split.f2.title', 'Enduro')}</b><span>{t('home.split.f2.text', "Per chi cerca l'adrenalina pura sui sentieri più tecnici.")}</span></span></li>
                <li><span className="t"><b>{t('home.split.f3.title', 'Percorsi speciali')}</b><span>{t('home.split.f3.text', 'Itinerari su misura con guida, assistenza e roadbook.')}</span></span></li>
              </ul>
              <Link to="/tour" className="btn btn-ghost-ink" style={{ marginTop: 34 }}>{t('home.split.cta', 'Tutti i tour')}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <CtaBand
        image={img('home.cta.image', '/img/insolita-sardegna-moto.jpg')}
        eyebrow={t('home.cta.eyebrow', 'Unisciti al club')}
        title={t('home.cta.title', 'Pronto a\npartire con noi?')}
        text={t('home.cta.text', 'Prenota la tua escursione in moto o richiedi informazioni sul tesseramento. Ti aspettiamo con il gas aperto e lo sguardo sull\'orizzonte.')}
      >
        <div className="band__phone">
          <span>{t('home.cta.phonelabel', 'Chiama ora')}</span>
          <a href={`tel:${t('contact.phone', '+393467814537')}`}>(+39) {t('contact.phone', '+393467814537').replace('+39', '')}</a>
        </div>
      </CtaBand>
    </>
  );
};

/** Evidenzia "tesori nascosti" in acid come nell'originale (<em>). */
function renderManifesto(text: string): React.ReactNode {
  const target = 'tesori nascosti';
  const idx = text.toLowerCase().indexOf(target);
  if (idx === -1) return renderMultiline(text);
  return (
    <>
      {text.slice(0, idx)}
      <em>{text.slice(idx, idx + target.length)}</em>
      {text.slice(idx + target.length)}
    </>
  );
}

export default Home;
